/**
 * Ligas firmadas de los archivos privados (CURP, foto y comprobante de la
 * inscripción, y la credencial del jugador o socio):
 * `/uploads/public-registrations/...?exp=<epoch>&sig=<firma>` (también
 * `/uploads/credentials/...` y `/uploads/club-credentials/...`).
 * El servidor las emite con 60–75 minutos de vida (las de credencial, hasta
 * el día siguiente al fin del torneo); `exp` dice cuándo vencen.
 *
 * Aquí vive lo que el navegador necesita saber de ellas:
 * - cuándo vence una liga (para decirlo al compartirla);
 * - cuándo una pantalla abierta debe pedir sus datos otra vez (ligas nuevas);
 * - cuándo el service worker NO debe responder con una copia vieja del API
 *   cuyas ligas ya vencieron.
 */

import { isPrivateUploadPath } from "./privateUploads";

/** Una liga que vence en menos de esto ya no se ofrece: se piden ligas nuevas. */
export const SIGNED_LINK_REFRESH_MARGIN_MS = 15 * 60 * 1000;

// `exp` seguido de `sig`, también dentro de un JSON (`&` puede venir como &).
const SIGNED_LINK_IN_TEXT =
  /\/uploads\/+(?:public-registrations|credentials|club-credentials)\/[^"'\s?]*\?exp=(\d{1,12})(?:&|\\u0026)sig=/gi;

function parseUrl(url: string): URL | null {
  try {
    const base = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    return new URL(url, base);
  } catch {
    return null;
  }
}

/** Momento (ms) en que vence la liga firmada, o null si no es una liga firmada. */
export function signedLinkExpiryMs(url: string | null | undefined): number | null {
  if (!url) return null;
  const parsed = parseUrl(url);
  if (!parsed || !isPrivateUploadPath(parsed.pathname)) return null;
  const exp = parsed.searchParams.get("exp");
  if (!exp || !/^\d{1,12}$/.test(exp) || !parsed.searchParams.get("sig")) return null;
  return Number(exp) * 1000;
}

/** La primera caducidad (ms) entre varias ligas; null si ninguna es firmada. */
export function earliestSignedLinkExpiryMs(urls: Iterable<string | null | undefined>): number | null {
  let earliest: number | null = null;
  for (const url of urls) {
    const expiry = signedLinkExpiryMs(url);
    if (expiry !== null && (earliest === null || expiry < earliest)) earliest = expiry;
  }
  return earliest;
}

/** La primera caducidad (ms) de las ligas firmadas dentro de un texto (JSON del API). */
export function earliestSignedLinkExpiryInText(text: string): number | null {
  let earliest: number | null = null;
  for (const match of text.matchAll(SIGNED_LINK_IN_TEXT)) {
    const expiry = Number(match[1]) * 1000;
    if (earliest === null || expiry < earliest) earliest = expiry;
  }
  return earliest;
}

/** ¿Ya venció o está por vencer (dentro del margen)? */
export function isSignedLinkStale(expiryMs: number | null, nowMs: number = Date.now()): boolean {
  return expiryMs !== null && expiryMs - nowMs < SIGNED_LINK_REFRESH_MARGIN_MS;
}

type PluginState = { networkFailed?: boolean };

/**
 * Plugin de Workbox para el caché del API (NetworkFirst con 3 s de espera).
 *
 * Si la red tarda, Workbox responde con la copia guardada, que puede ser de
 * ayer: sus ligas firmadas ya vencieron y cada «Ver» daría «esta liga ya
 * venció». Con este plugin esa copia NO se usa y se espera la respuesta de
 * la red (ligas nuevas). Sin red (falló la petición) sí se usa la copia: ver
 * los datos viejos sin conexión es mejor que no ver nada.
 */
export function signedLinksFreshnessPlugin(now: () => number = () => Date.now()) {
  return {
    fetchDidFail: async ({ state }: { state?: PluginState }) => {
      if (state) state.networkFailed = true;
    },
    cachedResponseWillBeUsed: async ({
      cachedResponse,
      state,
    }: {
      cachedResponse?: Response | null;
      state?: PluginState;
    }): Promise<Response | null> => {
      if (!cachedResponse) return null;
      if (state?.networkFailed) return cachedResponse;
      try {
        const text = await cachedResponse.clone().text();
        if (isSignedLinkStale(earliestSignedLinkExpiryInText(text), now())) return null;
      } catch {
        // Si no se puede leer, se usa como siempre.
      }
      return cachedResponse;
    },
  };
}

const CDMX_TIME = (() => {
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
})();

/** Hora (CDMX, 24 h) en que vence una liga: "11:00". */
export function formatSignedLinkTime(expiryMs: number): string {
  const date = new Date(expiryMs);
  if (CDMX_TIME) {
    const parts: Record<string, string> = {};
    for (const part of CDMX_TIME.formatToParts(date)) parts[part.type] = part.value;
    if (parts.hour && parts.minute) return `${parts.hour.padStart(2, "0")}:${parts.minute.padStart(2, "0")}`;
  }
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

const CDMX_DATE = (() => {
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return null;
  }
})();

/** Fecha (CDMX) en que vence una liga: "15/11/2026". */
export function formatSignedLinkDate(expiryMs: number): string {
  const date = new Date(expiryMs);
  if (CDMX_DATE) {
    const parts: Record<string, string> = {};
    for (const part of CDMX_DATE.formatToParts(date)) parts[part.type] = part.value;
    if (parts.day && parts.month && parts.year) return `${parts.day.padStart(2, "0")}/${parts.month.padStart(2, "0")}/${parts.year}`;
  }
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

function remainingText(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  if (minutes < 120) return minutes === 1 ? "1 minuto" : `${minutes} minutos`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} horas`;
  const days = Math.floor(hours / 24);
  return `${days} días`;
}

/**
 * "Vence el 15/11/2026 a las 23:59 (en 12 días)." — la fecha y el tiempo REAL
 * que le queda a la liga (las de credencial duran hasta que termina el
 * torneo; las de CURP, foto y comprobante, una hora).
 */
export function signedLinkExpiryPhrase(expiryMs: number, nowMs: number = Date.now()): string {
  return `Vence el ${formatSignedLinkDate(expiryMs)} a las ${formatSignedLinkTime(expiryMs)} (en ${remainingText(expiryMs - nowMs)}).`;
}
